// Engineering Division — Olu-Akintade Computer & Electrical Works Engineering
// Full public site with dedicated routes for the main landing AND every service detail page.
// Strict adherence to the supplied content — nothing is summarised.
import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import {
  ChevronRight, CheckCircle2, ShieldCheck, Award, Wrench,
  Laptop, Plug, Tv, ShoppingCart, Cog, Network, Building, ClipboardCheck,
  ArrowRight, Phone, Mail, Sparkles,
} from 'lucide-react';
import PublicSiteShell from '@/components/layout/PublicSiteShell';
import EngineeringContactForm from '@/components/engineering/EngineeringContactForm';
import { ENGINEERING_BRAND, ENGINEERING_SERVICES } from '@/lib/publicSiteContent';

const ICON_BY_SLUG = {
  'computer-repairs': Laptop,
  'electrical-installations': Plug,
  'electronics-repairs': Tv,
  'cctv-security': ShieldCheck,
  'networking-it': Network,
  'industrial-equipment': Cog,
  'sales-supply': ShoppingCart,
  'maintenance-contracts': ClipboardCheck,
};

const ENGINEERING_MENU = [
  { label: 'Home', href: '/engineering' },
  {
    label: 'Services',
    dropdown: ENGINEERING_SERVICES.map((s) => ({
      label: s.title,
      href: `/engineering/${s.slug}`,
    })),
  },
  { label: 'About', href: '/about/about-attp' },
  { label: 'Why Us', href: '/engineering#why' },
  { label: 'Contact', href: '/engineering#contact' },
];

/* ================================================================== */
/*  ENGINEERING — MAIN LANDING                                         */
/* ================================================================== */
export default function EngineeringMainPage() {
  const b = ENGINEERING_BRAND;
  return (
    <PublicSiteShell brand={b} menu={ENGINEERING_MENU} chatContext="Engineering visitor — Olu-Akintade Engineering site.">
      {/* HERO */}
      <section
        id="home"
        className="relative overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${b.primary} 0%, #1a2540 100%)`, color: '#FFFFFF' }}
        data-testid="engineering-hero"
      >
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full blur-[120px]" style={{ background: `${b.accent}25` }} />
        <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] rounded-full blur-[120px]" style={{ background: `${b.secondary}25` }} />
        <div className="container-x relative pt-20 pb-24 lg:pt-28 lg:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.18em] mb-6" style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}>
              <span className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ background: b.accent }} />
              CAC Registered · Government Approved · ATTP Engineering Division
            </div>
            <h1 className="font-display font-bold tracking-tight text-4xl sm:text-5xl lg:text-[58px] leading-[1.05] text-balance">
              {b.legalName.split('Engineering')[0]}
              <span style={{ color: b.accent }}>Engineering.</span>
            </h1>
            <p className="mt-6 text-lg leading-relaxed max-w-2xl" style={{ color: 'rgba(255,255,255,0.78)' }}>
              Professional electrical installation, computer repairs, electronics servicing, CCTV and security, networking and IT infrastructure, industrial equipment maintenance, sales & supply, and annual maintenance contracts — delivered with the reliability and expertise institutions and households can count on.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#services" className="px-6 py-3 rounded-md font-semibold text-sm inline-flex items-center gap-2 transition-transform hover:scale-[1.03]" style={{ background: b.accent, color: b.primary }} data-testid="engineering-cta-services">
                View Our Services <ChevronRight className="w-4 h-4" />
              </a>
              <a href="#contact" className="px-6 py-3 rounded-md font-semibold text-sm border border-white/25 text-white hover:bg-white/10 transition-colors">
                Request a Service
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] rounded-md overflow-hidden border shadow-2xl" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
              <img src="https://images.unsplash.com/photo-1581094288338-2314dddb7ece?w=1200&q=80" alt="Engineering at work" className="w-full h-full object-cover" />
              <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${b.primary}, transparent)` }} />
              <div className="absolute bottom-6 left-6 right-6 backdrop-blur-md bg-white/10 border border-white/20 rounded-md p-4">
                <div className="text-[10px] uppercase tracking-[0.16em] text-white/60 mb-1">Service area</div>
                <div className="text-sm text-white/95 font-medium">Nationwide deployment across Nigeria · regional partnerships across West Africa</div>
              </div>
            </div>
          </div>
        </div>

        <div className="relative border-t border-white/10 bg-black/30 py-5">
          <div className="container-x flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-xs uppercase tracking-[0.18em]" style={{ color: 'rgba(255,255,255,0.55)' }}>
            <span>Electrical</span><span>·</span>
            <span>Computers</span><span>·</span>
            <span>Electronics</span><span>·</span>
            <span>CCTV & Security</span><span>·</span>
            <span>Networking</span><span>·</span>
            <span>Industrial Systems</span>
          </div>
        </div>
      </section>

      {/* MISSION VISION VALUES */}
      <section id="why" className="py-20 bg-white">
        <div className="container-x">
          <div className="max-w-3xl mb-12">
            <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: b.accent }}>What we stand for</div>
            <h2 className="font-display font-bold text-4xl tracking-tight leading-[1.05]">
              Built on trust. Driven by engineering excellence.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="p-7 rounded-md border" style={{ borderColor: '#E5E7EB' }}>
              <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: b.accent }}>Mission</div>
              <p className="text-neutral-700 leading-relaxed">To deliver reliable, efficient and high-quality engineering solutions that meet the needs of individuals, businesses and industries — every project documented, tested and guaranteed.</p>
            </div>
            <div className="p-7 rounded-md text-white" style={{ background: b.primary }}>
              <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: b.accent }}>Vision</div>
              <p className="leading-relaxed" style={{ color: 'rgba(255,255,255,0.85)' }}>To become a leading engineering service provider in West Africa — known for excellence, innovation, durability and trust across every discipline we touch.</p>
            </div>
            <div className="p-7 rounded-md border" style={{ borderColor: '#E5E7EB' }}>
              <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: b.accent }}>Core Values</div>
              <ul className="space-y-1.5 text-sm text-neutral-700">
                <li className="flex items-start gap-2"><span className="h-1.5 w-1.5 rounded-full mt-2" style={{ background: b.accent }} />Integrity</li>
                <li className="flex items-start gap-2"><span className="h-1.5 w-1.5 rounded-full mt-2" style={{ background: b.accent }} />Professionalism</li>
                <li className="flex items-start gap-2"><span className="h-1.5 w-1.5 rounded-full mt-2" style={{ background: b.accent }} />Excellence</li>
                <li className="flex items-start gap-2"><span className="h-1.5 w-1.5 rounded-full mt-2" style={{ background: b.accent }} />Reliability</li>
                <li className="flex items-start gap-2"><span className="h-1.5 w-1.5 rounded-full mt-2" style={{ background: b.accent }} />Customer Satisfaction</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES OVERVIEW */}
      <section id="services" className="py-20 lg:py-24" style={{ background: '#F7F7F4' }}>
        <div className="container-x">
          <div className="max-w-3xl mb-12">
            <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: b.accent }}>Engineering Services</div>
            <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight leading-[1.05] text-balance">
              Eight specialised practice areas. One trusted engineering team.
            </h2>
            <p className="mt-5 text-lg text-neutral-600 leading-relaxed">
              From a single laptop screen replacement to a multi-building electrical installation, from a small office CCTV system to a campus-wide fibre network — our specialist teams deliver engineering work that lasts.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {ENGINEERING_SERVICES.map((s) => {
              const Icon = ICON_BY_SLUG[s.slug] || Wrench;
              return (
                <Link
                  key={s.slug}
                  to={`/engineering/${s.slug}`}
                  className="group p-6 bg-white rounded-md border transition-all hover:-translate-y-1 hover:shadow-xl"
                  style={{ borderColor: '#E5E7EB' }}
                  data-testid={`engineering-service-card-${s.slug}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="h-12 w-12 rounded-md flex items-center justify-center" style={{ background: `${b.accent}15`, color: b.accent }}>
                      <Icon className="w-6 h-6" strokeWidth={1.6} />
                    </div>
                    <ArrowRight className="w-4 h-4 text-neutral-300 group-hover:translate-x-1 transition-all" />
                  </div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold mb-2" style={{ color: b.accent }}>Service</div>
                  <h3 className="font-display font-semibold text-lg mb-2 leading-tight">{s.title}</h3>
                  <p className="text-sm text-neutral-600 leading-relaxed">{s.short}</p>
                  <div className="mt-4 pt-3 border-t text-xs text-neutral-500" style={{ borderColor: '#F1F1F1' }}>
                    {s.items.length} services in this practice
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-20 bg-white">
        <div className="container-x">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: b.accent }}>Why work with us</div>
              <h2 className="font-display font-bold text-3xl lg:text-4xl tracking-tight leading-tight">
                Engineering you can stake your operation on.
              </h2>
              <p className="mt-4 text-neutral-600 leading-relaxed">
                We have served households, schools, estates, corporate offices and industrial operators since our founding — building a reputation for honest diagnostics, durable installations and dependable after-sales support.
              </p>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { t: 'Certified engineers', d: 'Qualified technicians with documented credentials.' },
                { t: 'Documented work', d: 'Every installation comes with test certificates and as-built drawings.' },
                { t: 'Genuine parts', d: 'Original components sourced from authorised distributors.' },
                { t: 'Written warranty', d: 'Every job carries a clear, written warranty on workmanship.' },
                { t: 'Rapid response', d: 'Priority deployment for contracted clients across our coverage areas.' },
                { t: 'Transparent pricing', d: 'Quotations are structured, itemised and explained — no surprises.' },
              ].map((x) => (
                <div key={x.t} className="p-5 rounded-md border" style={{ borderColor: '#E5E7EB' }}>
                  <div className="font-display font-semibold text-base mb-1" style={{ color: b.primary }}>{x.t}</div>
                  <p className="text-sm text-neutral-600 leading-relaxed">{x.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT FORM (submits to Super Admin inbox) */}
      <EngineeringContactForm id="contact" />
    </PublicSiteShell>
  );
}

/* ================================================================== */
/*  ENGINEERING — DETAIL ROUTER (per-service page)                    */
/* ================================================================== */
export function EngineeringServiceRouter() {
  const { slug } = useParams();
  const service = ENGINEERING_SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/engineering" replace />;
  return <EngineeringServiceDetail service={service} />;
}

function EngineeringServiceDetail({ service }) {
  const b = ENGINEERING_BRAND;
  const Icon = ICON_BY_SLUG[service.slug] || Wrench;
  const related = ENGINEERING_SERVICES.filter((s) => s.slug !== service.slug).slice(0, 3);

  return (
    <PublicSiteShell brand={b} menu={ENGINEERING_MENU} chatContext={`Engineering visitor — viewing ${service.title}.`}>
      {/* Service hero */}
      <section className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${b.primary} 0%, #1a2540 100%)`, color: '#FFFFFF' }} data-testid={`service-hero-${service.slug}`}>
        <div className="absolute -top-32 -right-32 w-[400px] h-[400px] rounded-full blur-[120px]" style={{ background: `${b.accent}25` }} />
        <div className="container-x relative pt-16 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-4 inline-flex items-center gap-2" style={{ color: b.accent }}>
              <Link to="/engineering" className="hover:underline">Engineering</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <span>Services</span>
            </div>
            <div className="inline-flex items-center gap-3 mb-5">
              <div className="h-14 w-14 rounded-md flex items-center justify-center" style={{ background: `${b.accent}20`, color: b.accent }}>
                <Icon className="w-7 h-7" strokeWidth={1.5} />
              </div>
            </div>
            <h1 className="font-display font-bold tracking-tight text-4xl lg:text-5xl leading-[1.05]">
              {service.title}
            </h1>
            <p className="mt-5 text-lg leading-relaxed max-w-2xl" style={{ color: 'rgba(255,255,255,0.78)' }}>
              {service.intro}
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="aspect-[4/3] rounded-md overflow-hidden border shadow-2xl" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
              <img src={service.hero} alt={service.title} className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Detail body */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: b.accent }}>Practice overview</div>
            <h2 className="font-display font-bold text-2xl lg:text-3xl mb-5 leading-tight">{service.short}</h2>
            <p className="text-base text-neutral-700 leading-relaxed">{service.body}</p>

            <div className="mt-10">
              <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-4" style={{ color: b.accent }}>What we deliver</div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {service.items.map((item) => (
                  <li key={item} className="flex items-start gap-2.5 p-3 rounded-md border" style={{ borderColor: '#EEEEEE' }}>
                    <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: b.accent }} />
                    <span className="text-sm text-neutral-700 leading-snug">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-10 p-6 rounded-md" style={{ background: '#F7F7F4' }}>
              <p className="text-base text-neutral-700 leading-relaxed italic">&ldquo;{service.closing}&rdquo;</p>
            </div>
          </div>

          <aside className="lg:col-span-5 lg:sticky lg:top-28 self-start space-y-4">
            <div className="p-6 rounded-md text-white" style={{ background: b.primary }}>
              <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: b.accent }}>Engage this service</div>
              <div className="font-display font-bold text-xl mb-4">Talk to our engineering team</div>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-2.5"><Mail className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: b.accent }} /><div><div className="text-white/95">{b.email}</div><div className="text-[11px]" style={{ color: 'rgba(255,255,255,0.55)' }}>Engineering enquiries</div></div></li>
                <li className="flex items-start gap-2.5"><Phone className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: b.accent }} /><div><div className="text-white/95">{b.phone}</div><div className="text-[11px]" style={{ color: 'rgba(255,255,255,0.55)' }}>Direct line</div></div></li>
              </ul>
              <Link to="/engineering#contact" className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-md font-semibold text-sm" style={{ background: b.accent, color: b.primary }} data-testid={`service-cta-${service.slug}`}>
                Request a Service <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="p-6 rounded-md border" style={{ borderColor: '#E5E7EB' }}>
              <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: b.accent }}>Trust signals</div>
              <ul className="space-y-2 text-sm text-neutral-700">
                <li className="flex items-start gap-2"><Award className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: b.accent }} />CAC-registered, government-approved engineering firm</li>
                <li className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: b.accent }} />Written warranty on every job we deliver</li>
                <li className="flex items-start gap-2"><Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: b.accent }} />Genuine parts from authorised distributors</li>
                <li className="flex items-start gap-2"><Building className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: b.accent }} />References available across schools, estates and corporates</li>
              </ul>
            </div>
          </aside>
        </div>
      </section>

      {/* Related services */}
      <section className="py-16 lg:py-20" style={{ background: '#F7F7F4' }}>
        <div className="container-x">
          <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: b.accent }}>Related services</div>
          <h3 className="font-display font-bold text-2xl lg:text-3xl mb-8 leading-tight">Explore more of our engineering practice</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {related.map((s) => {
              const RIcon = ICON_BY_SLUG[s.slug] || Wrench;
              return (
                <Link key={s.slug} to={`/engineering/${s.slug}`} className="group p-6 bg-white rounded-md border hover:-translate-y-1 hover:shadow-xl transition-all" style={{ borderColor: '#E5E7EB' }}>
                  <div className="h-11 w-11 rounded-md flex items-center justify-center mb-3" style={{ background: `${b.accent}15`, color: b.accent }}>
                    <RIcon className="w-5 h-5" strokeWidth={1.6} />
                  </div>
                  <h4 className="font-display font-semibold text-base mb-1">{s.title}</h4>
                  <p className="text-sm text-neutral-600 leading-relaxed">{s.short}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <EngineeringContactForm serviceSlug={service.slug} serviceTitle={service.title} id="contact" />
    </PublicSiteShell>
  );
}

/* ------------------------------------------------------------------ */
// Legacy ContactBlock removed — replaced by <EngineeringContactForm />
